/**
 * Parse element and extract editable properties
 * This identifies what can be edited based on the element type and content
 */
export function parseElement(element) {
  if (!element) return null;

  const computedStyle = window.getComputedStyle(element);
  const tagName = element.tagName.toLowerCase();
  
  // Extract data path if available
  const dataPath = element.getAttribute('data-path');
  const dataType = element.getAttribute('data-type');
  const dataIndex = element.getAttribute('data-index');
  
  // Build element info
  const elementInfo = {
    element,
    tagName,
    dataPath,
    dataType,
    dataIndex,
    rect: element.getBoundingClientRect(),
    
    // Content
    textContent: element.textContent?.trim(),
    innerHTML: element.innerHTML,
    
    // Computed styles
    styles: {
      // Typography
      fontSize: computedStyle.fontSize,
      fontWeight: computedStyle.fontWeight,
      fontFamily: computedStyle.fontFamily,
      lineHeight: computedStyle.lineHeight,
      letterSpacing: computedStyle.letterSpacing,
      textAlign: computedStyle.textAlign,
      color: computedStyle.color,
      
      // Colors & Backgrounds
      backgroundColor: computedStyle.backgroundColor,
      borderColor: computedStyle.borderColor,
      
      // Spacing
      margin: computedStyle.margin,
      marginTop: computedStyle.marginTop,
      marginRight: computedStyle.marginRight,
      marginBottom: computedStyle.marginBottom,
      marginLeft: computedStyle.marginLeft,
      
      padding: computedStyle.padding,
      paddingTop: computedStyle.paddingTop,
      paddingRight: computedStyle.paddingRight,
      paddingBottom: computedStyle.paddingBottom,
      paddingLeft: computedStyle.paddingLeft,
      
      // Layout
      display: computedStyle.display,
      flexDirection: computedStyle.flexDirection,
      justifyContent: computedStyle.justifyContent,
      alignItems: computedStyle.alignItems,
      gap: computedStyle.gap,
      
      // Sizing
      width: computedStyle.width,
      height: computedStyle.height,
      maxWidth: computedStyle.maxWidth,
      maxHeight: computedStyle.maxHeight,
      
      // Borders & Radius
      borderRadius: computedStyle.borderRadius,
      borderWidth: computedStyle.borderWidth,
      
      // Shadow
      boxShadow: computedStyle.boxShadow,
    },
    
    // Classes
    classList: Array.from(element.classList),
    
    // Editable properties (determined by element type)
    editableProperties: determineEditableProperties(element, tagName, dataType),
  };
  
  // Extract image source if it's an image
  if (tagName === 'img') {
    elementInfo.src = element.getAttribute('src');
    elementInfo.alt = element.getAttribute('alt');
  }
  
  return elementInfo;
}

/**
 * Determine which properties can be edited for this element
 */
function determineEditableProperties(element, tagName, dataType) {
  const properties = {
    text: false,
    color: false,
    backgroundColor: false,
    fontSize: false,
    fontWeight: false,
    fontFamily: false,
    spacing: false,
    layout: false,
    image: false,
  };
  
  // Text elements
  if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'a', 'button', 'li'].includes(tagName)) {
    properties.text = true;
    properties.color = true;
    properties.fontSize = true;
    properties.fontWeight = true;
    properties.fontFamily = true;
  }
  
  // Images
  if (tagName === 'img') {
    properties.image = true;
  }
  
  // Containers - always allow spacing and layout
  if (['div', 'section', 'article', 'header', 'footer', 'nav'].includes(tagName)) {
    properties.spacing = true;
    properties.layout = true;
    properties.backgroundColor = true;
  }
  
  // Data-driven detection
  if (dataType) {
    switch (dataType) {
      case 'text':
      case 'headline':
      case 'title':
        properties.text = true;
        properties.color = true;
        properties.fontSize = true;
        properties.fontWeight = true;
        break;
      case 'image':
      case 'logo':
        properties.image = true;
        break;
      case 'container':
        properties.spacing = true;
        properties.layout = true;
        properties.backgroundColor = true;
        break;
    }
  }
  
  return properties;
}

/**
 * Extract Tailwind classes from element
 */
export function extractTailwindClasses(element) {
  const classList = Array.from(element.classList);
  
  return {
    spacing: {
      margin: classList.filter(c => c.match(/^-?m[trblxy]?-/)),
      padding: classList.filter(c => c.match(/^p[trblxy]?-/)),
    },
    typography: {
      fontSize: classList.find(c => c.match(/^text-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)$/)),
      fontWeight: classList.find(c => c.match(/^font-(thin|extralight|light|normal|medium|semibold|bold|extrabold|black)$/)),
      textAlign: classList.find(c => c.match(/^text-(left|center|right|justify)$/)),
      color: classList.find(c => c.match(/^text-/)),
    },
    background: {
      color: classList.find(c => c.match(/^bg-/)),
    },
    layout: {
      display: classList.find(c => c.match(/^(flex|grid|block|inline|hidden)$/)),
      flexDirection: classList.find(c => c.match(/^flex-(row|col)/)),
      justifyContent: classList.find(c => c.match(/^justify-/)),
      alignItems: classList.find(c => c.match(/^items-/)),
      gap: classList.find(c => c.match(/^gap-/)),
    },
  };
}

/**
 * Find closest editable parent element
 */
export function findEditableParent(element, maxDepth = 5) {
  let current = element;
  let depth = 0;
  
  while (current && depth < maxDepth) {
    // Check if element has data-path (makes it editable)
    if (current.getAttribute('data-path')) {
      return current;
    }
    
    // Check if it's a meaningful element
    const tagName = current.tagName.toLowerCase();
    if (['section', 'div', 'article', 'header', 'footer'].includes(tagName)) {
      // Check if it has an identifiable class or structure
      if (current.classList.length > 0) {
        return current;
      }
    }
    
    current = current.parentElement;
    depth++;
  }
  
  return element;
}

/**
 * Get element identifier for data path resolution
 */
export function getElementDataPath(element) {
  const dataPath = element.getAttribute('data-path');
  if (dataPath) return dataPath;
  
  // Try to infer from structure
  const dataType = element.getAttribute('data-type');
  const dataIndex = element.getAttribute('data-index');
  
  if (dataType && dataIndex !== null) {
    return `copy.${dataType}[${dataIndex}]`;
  }
  
  return null;
}

/**
 * Check if element is inside preview container
 */
export function isInPreviewContainer(element) {
  return element.closest('[data-preview-container]') !== null;
}

